import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Presentation } from "@lala/harness/protocol";
import { cn } from "cn";

import type { DatasetPayload } from "../dataset.ts";
import { formatBySemantic, formatTimestamp } from "./format.ts";

/**
 * `intent: "entity_card"`: los campos de la ÚNICA fila del dataset, más el
 * `timeline` del ciclo de vida de la entidad, si lo hay.
 */
export function EntityCard({
  presentation,
  dataset,
}: {
  presentation: Presentation;
  dataset: DatasetPayload;
}) {
  if (dataset.rows.length !== 1) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Se esperaba una entidad</AlertTitle>
        <AlertDescription>
          Llegaron {dataset.rows.length} filas en vez de una.
        </AlertDescription>
      </Alert>
    );
  }

  const row = dataset.rows[0] ?? {};

  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      {presentation.entity !== undefined && (
        <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {presentation.entity}
        </div>
      )}

      <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
        {presentation.columns.map((column) => (
          <div key={column.name}>
            <dt className="text-xs text-muted-foreground">{column.label}</dt>
            <dd
              className={cn(
                "font-medium",
                column.semantic === "entity_id" && "font-mono",
              )}
            >
              {formatBySemantic(row[column.name] ?? null, column.semantic)}
            </dd>
          </div>
        ))}
      </dl>

      {presentation.timeline !== undefined &&
        presentation.timeline.length > 0 && (
          <ol className="mt-4 grid gap-2 border-t pt-3">
            {presentation.timeline.map((entry, index) => (
              <li key={index} className="flex items-start gap-2 text-sm">
                <span
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full border",
                    entry.at !== null
                      ? "border-primary bg-primary"
                      : "border-muted-foreground",
                  )}
                />
                <div className="min-w-0">
                  <div className="font-medium">{entry.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {entry.at !== null
                      ? formatTimestamp(entry.at)
                      : "Pendiente"}
                    {entry.detail !== undefined && ` · ${entry.detail}`}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
    </div>
  );
}
